import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import CopilotSidePanel from "./CopilotSidePanel";
import { FitBadge, ReadBadge, buyerParts, withLiveDeadline } from "./CopilotTenderResultsCard";
import { DismissMenu, WhyChips } from "../TenderSearch/DiscoverCard";
import {
  DESCRIPTION_PROSE,
  DescriptionText,
  NoticePill,
  SetAsidePill,
  SourceBadge,
  URGENCY_TEXT,
  withRules,
} from "../TenderSearch/TenderUI";
import {
  deadlineUrgency,
  downloadTenderDocument,
  fetchTender,
  formatDaysRemaining,
  formatDeadline,
  importTender,
} from "../../../services/tendersApi";

/* One tender, beside the conversation.
 *
 * Opened from a row of Tender Search results in the chat. Enough to decide
 * whether to bid — who is buying, by when, what they want, what is attached —
 * with the one action that decision leads to pinned at the bottom. The full
 * tender page is a link away for everything else (contacts, codes, versions);
 * repeating it here would turn a quick look into a second copy of that page.
 *
 * The header renders at once from the row that was clicked, so opening is
 * instant; the description and attachments fill in when the detail arrives.
 */

// Lower than the tender page's: this is a 620px column beside a chat, not a page.
const DESCRIPTION_CLAMP_PX = 260;

const formatBytes = (n) => {
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const postedDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const Icon = ({ d, size = 14, stroke = 2.2, className = "" }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);
const CLOSE = "M18 6L6 18M6 6l12 12";
const EXTERNAL = "M7 17L17 7M9 7h8v8";
const DOWNLOAD = "M12 4v11m0 0l-4-4m4 4l4-4M5 20h14";
const FILE = "M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5";
const LINK = "M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1";

const Fact = ({ label, children }) =>
  children ? (
    <div className="min-w-0">
      <dt className="text-[12px] font-semibold uppercase tracking-wide text-[#77707E]">{label}</dt>
      <dd className="mt-0.5 break-words text-[15px] leading-snug text-[#18101C]">{children}</dd>
    </div>
  ) : null;

const SectionTitle = ({ children, meta }) => (
  <div className="mb-2 flex items-baseline justify-between gap-2">
    <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[#52525B]">{children}</h3>
    {meta && <span className="text-[12.5px] text-[#77707E]">{meta}</span>}
  </div>
);

// A span, so it can stand in for text inside a heading as well as a block.
const Skeleton = ({ className }) => (
  <span className={`block animate-pulse rounded bg-[#18101C]/[0.06] motion-reduce:animate-none ${className}`} />
);

const CHECK_MARK = {
  pass: { d: "M5 12.5l4.5 4.5L19 7.5", tone: "bg-[#E7F6EB] text-[#1F7D38]" },
  warn: { d: "M12 8v5M12 16.5v.01", tone: "bg-[#FEF3C7] text-[#B45309]" },
  unknown: { d: "M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5v.5M12 17v.01", tone: "bg-[#18101C]/[0.06] text-[#6B6472]" },
};

const OUTCOME = { win: "Won", won: "Won", lost: "Lost", loss: "Lost" };

/* Discover's breakdown: why this tender was picked, each requirement as a line
 * that passes, warns or is unknown, and the past bids it resembles. Only shown
 * for a tender that came from Discover (`initial.match`). */
const MatchBreakdown = ({ match, fit }) => (
  <section className="mb-5 rounded-xl border border-[#A04A97]/20 bg-gradient-to-b from-[#FBF4FA] to-white px-4 py-3.5">
    <div className="flex items-center gap-2">
      <FitBadge band={match.band} />
      <span className="text-[13.5px] font-semibold text-[#18101C]">
        {match.checks_passed} of {match.checks_total} checks pass
      </span>
    </div>
    {fit?.line && <p className="mt-2 text-[15px] leading-snug text-[#18101C]">{fit.line}</p>}
    {!!match.why?.length && (
      <div className="mt-3">
        <h4 className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-[#8E4187]">Why Sensa picked this</h4>
        <WhyChips why={match.why} limit={6} />
      </div>
    )}
    {!!match.checks?.length && (
      <div className="mt-3.5">
        <h4 className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-[#52525B]">Requirements check</h4>
        <ul className="space-y-1.5">
          {match.checks.map((c) => {
            const mark = CHECK_MARK[c.state] || CHECK_MARK.unknown;
            return (
              <li key={`${c.kind}-${c.text}`} className="flex items-start gap-2 text-[14.5px] leading-snug text-[#18101C]">
                <span className={`mt-[1px] grid h-5 w-5 flex-none place-items-center rounded-full ${mark.tone}`}>
                  <Icon d={mark.d} size={12} stroke={2.6} />
                </span>
                {c.text}
              </li>
            );
          })}
        </ul>
      </div>
    )}
    {!!match.precedent?.length && (
      <div className="mt-3.5">
        <h4 className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-[#52525B]">Like your past bids</h4>
        <ul className="space-y-0.5">
          {match.precedent.map((p) => (
            <li key={p.project} className="text-[14px] text-[#3F3F46]">
              {p.project}
              {p.outcome && <span className="text-[#77707E]"> · {OUTCOME[String(p.outcome).toLowerCase()] || p.outcome}</span>}
            </li>
          ))}
        </ul>
      </div>
    )}
  </section>
);

const CopilotTenderPanel = ({
  tenderId, initial, visible, onClose, onProjectCreated,
  // Discover only: Match N of M, and the feed's own actions.
  nav, onSave, onDismiss,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  // A ref, not the state: two clicks inside one render would both read
  // `importing` as false and start two imports.
  const busyRef = useRef(false);
  const descriptionRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError("");
    setExpanded(false);
    fetchTender(tenderId)
      .then((data) => alive && setDetail(data))
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err?.response?.status === 404
            ? "This tender is no longer in Tender Search."
            : "Couldn't load the details. Try again in a moment."
        );
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [tenderId]);

  const t = withLiveDeadline(detail || initial || {});
  // Only present when the tender came from a recommendation: the ranking's own
  // reasons, kept from the card row because the detail endpoint knows nothing
  // about why this tender was suggested.
  const fit = initial?.fit;
  const match = initial?.match || null;
  // A full check run earlier on the tender's full page, if any: it is THE
  // verdict, so it is shown in place of the quick match. The check itself is
  // not offered here — it lives on the full page.
  const fullRead = fit?.full_read || null;
  const urgency = deadlineUrgency(t);
  const closed = t.days_remaining != null && t.days_remaining < 0;
  const isHtml = detail?.description_format === "html";
  const attachments = (detail?.attachments || []).filter((d) => !d.access_restricted);
  const restricted = (detail?.attachments || []).length - attachments.length;
  const links = detail?.links || [];

  // Measured, like the tender page: a short description with a tall table
  // needs the control, and a long one of short lines may not.
  useLayoutEffect(() => {
    const node = descriptionRef.current;
    setOverflows(!!node && node.scrollHeight > DESCRIPTION_CLAMP_PX + 24);
  }, [detail?.description, detail?.description_format]);

  const startProject = async () => {
    if (busyRef.current || !t.id) return;
    busyRef.current = true;
    setImporting(true);
    try {
      const result = await importTender(t.id);
      toast.success("Project created. Documents are downloading in the background");
      onProjectCreated?.({ projectId: result.folder_id, projectName: result.folder_name || t.title });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not create the project. Please try again.");
      busyRef.current = false;
      setImporting(false);
    }
  };

  const download = async (doc) => {
    if (downloading) return;
    setDownloading(doc.id);
    try {
      await downloadTenderDocument(t.id, doc.id, doc.name);
    } catch (err) {
      toast.error(err?.message || "Could not download that file.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <CopilotSidePanel visible={visible} label={`Tender: ${t.title || "details"}`} onClose={onClose} width={620}>
      {/* ---- header ---- */}
      <header className="flex-none border-b border-[#18101C]/[0.07] px-5 pb-4 pt-4">
        <div className="flex items-center gap-2">
          {nav ? (
            <div className="flex flex-1 items-center gap-1">
              <button
                type="button"
                onClick={nav.onPrev}
                disabled={nav.index <= 0}
                aria-label="Previous match"
                className="grid h-7 w-7 place-items-center rounded-lg text-[#52525B] hover:bg-[#18101C]/[0.06] disabled:opacity-30"
              >
                <Icon d="M15 18l-6-6 6-6" />
              </button>
              <span className="text-[12.5px] font-semibold text-[#52525B]">
                Match {nav.index + 1} of {nav.total}
              </span>
              <button
                type="button"
                onClick={nav.onNext}
                disabled={nav.index >= nav.total - 1}
                aria-label="Next match"
                className="grid h-7 w-7 place-items-center rounded-lg text-[#52525B] hover:bg-[#18101C]/[0.06] disabled:opacity-30"
              >
                <Icon d="M9 18l6-6-6-6" />
              </button>
            </div>
          ) : (
            <p className="flex-1 text-[12px] font-semibold uppercase tracking-wide text-[#A04A97]">Tender</p>
          )}
          {t.id && (
            <a
              href={`/tender-search/${t.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[13px] font-medium text-[#52525B] transition-colors hover:bg-[#18101C]/[0.05] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
            >
              Full page
              <Icon d={EXTERNAL} size={11} stroke={2.4} />
            </a>
          )}
          {t.source_url && (
            <a
              href={t.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[13px] font-medium text-[#52525B] transition-colors hover:bg-[#18101C]/[0.05] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
            >
              {t.source_label || "Source"}
              <Icon d={EXTERNAL} size={11} stroke={2.4} />
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.06] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
          >
            <Icon d={CLOSE} />
          </button>
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <SourceBadge label={t.source_label} />
          <NoticePill type={t.notice_type} label={t.notice_type_label} />
          <SetAsidePill label={t.set_aside_label} />
        </div>

        <h2 className="mt-2.5 text-[20px] font-semibold leading-snug tracking-[-0.01em] text-[#18101C]">
          {t.title || <Skeleton className="h-5 w-3/4" />}
        </h2>
        {(t.buyer_path || t.buyer) && (
          <p className="mt-1 text-[14px] leading-snug text-[#52525B]">{buyerParts(t).join(" › ")}</p>
        )}

        {/* The deadline is the first thing a bid manager checks, so it gets the
            one block of colour in the header — urgency-tinted like the list. */}
        <div className="mt-3.5 flex items-center gap-4 rounded-xl border border-[#18101C]/[0.07] bg-white px-3.5 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="text-[12px] font-semibold uppercase tracking-wide text-[#77707E]">Closes</p>
            <p className={`text-[18px] font-semibold leading-tight ${URGENCY_TEXT[urgency]}`}>
              {formatDaysRemaining(t) || "No closing date"}
            </p>
          </div>
          <p className="text-right text-[14px] leading-snug text-[#3F3F46]">
            {formatDeadline(t)}
            {t.published_at && (
              <span className="block text-[12.5px] text-[#77707E]">Posted {postedDate(t.published_at)}</span>
            )}
          </p>
        </div>
      </header>

      {/* ---- body ---- */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {loadError && (
          <p className="mb-4 rounded-lg bg-[#FEF2F2] px-3 py-2 text-[14px] text-[#B91C1C]">{loadError}</p>
        )}

        {match && <MatchBreakdown match={match} fit={fit} />}

        {fit && (!match || fullRead) && (
          <section className="mb-5 rounded-xl border border-[#18101C]/[0.07] bg-white px-4 py-3">
            <div className="flex items-center gap-2">
              <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[#52525B]">
                {fullRead ? "Full check" : "Quick match"}
              </h3>
              {fullRead ? <ReadBadge verdict={fullRead.verdict} /> : <FitBadge band={fit.band} />}
            </div>
            <ul className="mt-2 space-y-1">
              {((fullRead ? fullRead.reasons : fit.reasons) || []).map((r) => (
                <li key={r} className="flex gap-2 text-[15px] leading-snug text-[#18101C]">
                  <span aria-hidden="true" className="mt-[8px] h-1.5 w-1.5 flex-none rounded-full bg-[#1F7D38]" />
                  {r}
                </li>
              ))}
              {((fullRead ? fullRead.youd_need : fit.cautions) || []).map((c) => (
                <li key={c} className="flex gap-2 text-[15px] leading-snug text-[#A16207]">
                  <span aria-hidden="true" className="mt-[8px] h-1.5 w-1.5 flex-none rounded-full bg-[#D97706]" />
                  {c}
                </li>
              ))}
            </ul>
            <p className="mt-2.5 text-[13px] text-[#77707E]">
              {fullRead
                ? "From the full check run on this tender's full page."
                : "A quick read of the notice against your Memory. The full check is on the tender's full page."}
            </p>
          </section>
        )}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Fact label="Solicitation no.">{t.reference}</Fact>
          <Fact label="Place of performance">{t.place}</Fact>
          <Fact label="Status">{t.status_label}</Fact>
          <Fact label="Notice type">{t.notice_type_label}</Fact>
        </dl>

        <section className="mt-6">
          <SectionTitle>Description</SectionTitle>
          {loading && !detail ? (
            <div className="space-y-2">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-11/12" />
              <Skeleton className="h-3.5 w-4/5" />
              <Skeleton className="h-3.5 w-2/3" />
            </div>
          ) : detail?.description ? (
            <div>
              {/* The fade covers the text only. Laid over the whole block, it
                  also washed out the button below, which then read as disabled. */}
              <div className="relative">
                <div
                  ref={descriptionRef}
                  style={overflows && !expanded ? { maxHeight: DESCRIPTION_CLAMP_PX, overflow: "hidden" } : undefined}
                  className={`${DESCRIPTION_PROSE} !text-[15px] !text-[#3F3F46] [&_table]:block [&_table]:overflow-x-auto`}
                  // Sanitised server-side against a fixed allow-list on the way
                  // in — the same markup the tender page renders.
                  {...(isHtml ? { dangerouslySetInnerHTML: { __html: withRules(detail.description) } } : {})}
                >
                  {isHtml ? undefined : <DescriptionText text={detail.description} />}
                </div>
                {overflows && !expanded && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#FCFBFC] to-transparent"
                  />
                )}
              </div>
              {overflows && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-[#A04A97]/30 bg-white px-3.5 py-2 text-[14px] font-semibold text-[#8E4187] transition-colors hover:bg-[#A04A97]/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
                >
                  {expanded ? "Show less" : "Show the full description"}
                  <Icon d="M6 9l6 6 6-6" size={14} stroke={2.4} className={expanded ? "rotate-180" : ""} />
                </button>
              )}
            </div>
          ) : (
            <p className="text-[14px] text-[#77707E]">
              {detail?.description_available === false
                ? `The buyer didn't publish a description. Check ${t.source_label || "the source"} for details.`
                : "No description yet."}
            </p>
          )}
        </section>

        <section className="mt-6">
          <SectionTitle
            meta={attachments.length ? `${attachments.length} file${attachments.length === 1 ? "" : "s"}` : null}
          >
            Documents
          </SectionTitle>
          {loading && !detail ? (
            <Skeleton className="h-10 w-full" />
          ) : attachments.length || links.length ? (
            <ul className="space-y-1.5">
              {attachments.map((doc) => (
                <li key={doc.id}>
                  <button
                    type="button"
                    onClick={() => download(doc)}
                    disabled={downloading === doc.id}
                    className="group flex w-full items-center gap-2.5 rounded-lg border border-[#18101C]/[0.07] bg-white px-3 py-2 text-left transition-colors hover:border-[#A04A97]/30 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
                  >
                    <span className="flex-none text-[#9CA3AF]">
                      <Icon d={FILE} size={15} stroke={1.9} />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[14px] text-[#18101C]">{doc.name}</span>
                    <span className="flex-none text-[12px] uppercase text-[#77707E]">
                      {[doc.file_type, formatBytes(doc.size_bytes)].filter(Boolean).join(" · ")}
                    </span>
                    <span className="flex-none text-[#A04A97]/50 transition-colors group-hover:text-[#A04A97]">
                      {downloading === doc.id ? (
                        <span className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#A04A97]/25 border-t-[#A04A97] motion-reduce:animate-none" />
                      ) : (
                        <Icon d={DOWNLOAD} size={14} />
                      )}
                    </span>
                  </button>
                </li>
              ))}
              {links.map((link) => (
                <li key={link.id || link.url}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2.5 rounded-lg border border-[#18101C]/[0.07] bg-white px-3 py-2 transition-colors hover:border-[#A04A97]/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
                  >
                    <span className="flex-none text-[#9CA3AF]">
                      <Icon d={LINK} size={15} stroke={1.9} />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[14px] text-[#18101C]">{link.name || link.url}</span>
                    <span className="flex-none text-[#A04A97]/50 group-hover:text-[#A04A97]">
                      <Icon d={EXTERNAL} size={12} stroke={2.4} />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[14px] text-[#77707E]">
              {t.has_documents === null
                ? "The attachment list isn't known yet. Starting a project looks them up and downloads what it finds."
                : "No attachments were published with this tender."}
            </p>
          )}
          {restricted > 0 && (
            <p className="mt-2 text-[13px] text-[#77707E]">
              {restricted} more {restricted === 1 ? "file is" : "files are"} restricted and must be requested from{" "}
              {t.source_label || "the source"}.
            </p>
          )}
        </section>
      </div>

      {/* ---- the decision ---- */}
      <footer className="flex-none border-t border-[#18101C]/[0.07] bg-white px-5 py-3.5">
        {closed && (
          <p className="mb-2 text-[14px] font-medium text-[#A16207]">This tender has closed.</p>
        )}
        {t.is_imported && (
          <p className="mb-2 text-[14px] text-[#15803D]">
            Already in a project in your workspace. Starting again makes a second one.
          </p>
        )}
        {match && (onSave || onDismiss) && (
          <div className="relative mb-2.5 flex gap-2">
            <button
              type="button"
              onClick={() => onSave?.(initial, !match.saved)}
              className="flex-1 rounded-xl border border-[#A04A97]/35 bg-white px-3 py-2.5 text-[14px] font-semibold text-[#8E4187] transition-colors hover:bg-[#A04A97]/[0.06]"
            >
              {match.saved ? "Saved ✓" : "Save for later"}
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              className="flex-1 rounded-xl border border-[#18101C]/15 bg-white px-3 py-2.5 text-[14px] font-semibold text-[#52525B] transition-colors hover:bg-[#18101C]/[0.04]"
            >
              Not a fit
            </button>
            {menuOpen && (
              <DismissMenu
                up
                onClose={() => setMenuOpen(false)}
                onPick={(reason) => {
                  setMenuOpen(false);
                  onDismiss?.(initial, reason);
                }}
              />
            )}
          </div>
        )}
        <button
          type="button"
          onClick={startProject}
          disabled={importing || !t.id}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#A04A97] px-4 py-3 text-[15px] font-semibold text-white transition-colors hover:bg-[#8E4187] disabled:cursor-not-allowed disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
        >
          {importing && (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none" />
          )}
          {importing ? "Creating the project…" : "Start a project from this tender"}
        </button>
        <p className="mt-1.5 text-center text-[12.5px] text-[#77707E]">
          {t.has_documents === false && !attachments.length
            ? "Named after the tender. No attachments were published, so the project starts empty."
            : "Named after the tender. Its documents download into it in the background."}
        </p>
      </footer>
    </CopilotSidePanel>
  );
};

export default CopilotTenderPanel;
